import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { MessageList }                        from 'react-chat-elements';
import { Form, Button, Spinner }              from 'react-bootstrap';
import { TelephoneFill }                      from 'react-bootstrap-icons';
import axios                                  from '../axiosAlertConfig';
import 'react-chat-elements/dist/main.css';
import { UserContext }                        from '../../userAlertContext';
import useOperationalUpdates from '../../hooks/useOperationalUpdates';

// promisified geolocation
const getPosition = () =>
  new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 5000,
    }),
  );

const cleanPhone = value => {
  const phone = String(value ?? '').trim();
  return /^(null|undefined)$/i.test(phone) ? '' : phone;
};

const getMessagePhone = row =>
  cleanPhone(
    row?.senderMobileNumber ||
    row?.phoneNumber ||
    row?.mobileNumber ||
    row?.phone ||
    '',
  );

const toTelHref = value => {
  const phone = cleanPhone(value);
  if (!phone) return '';

  const prefix = phone.startsWith('+') ? '+' : '';
  const digits = phone.replace(/\D/g, '');
  return digits ? `tel:${prefix}${digits}` : '';
};


/* helper: DB row → react-chat‑elements item */
const mapRow = (row, me = 'Me', buildingName = '', myLocation = '' ) => {
  if (!row) return null;
  const mine   = (row.senderName || me) === me;
  const mobile = getMessagePhone(row);
  const telHref = toTelHref(mobile);

  // decide bubble type
  let type = 'text', data;
  if (row.attachmentURL) {
    if (row.attachmentType === 'Photo') {
      type = 'photo';
      data = { uri: row.attachmentURL };
    } else {
      type = 'file';
      data = {
        link: row.attachmentURL,
        fileName: row.attachmentURL.split('/').pop(),
      };
    }
  }

  // build a two‑line title: name + (building | location)
  const title = (
    <div>
      <div className="d-flex align-items-center gap-1">
        {telHref && (
          <a
            href={telHref}
            onClick={e => e.stopPropagation()}
            title={`Call ${mobile}`}
            aria-label={`Call ${row.senderName || 'message sender'} at ${mobile}`}
            style={{ textDecoration: 'none' }}
          >
            <TelephoneFill size={12} />
          </a>
        )}
        {row.senderName || (mine ? me : 'Unknown')}
      </div>
      <div style={{ fontSize: '0.75em', color: '#666', marginTop: 2 }}>
        {(buildingName || '—')}
        {myLocation ? ` | ${myLocation}` : ''}
      </div>

     {/* {statuses.length > 0 && ( */}
       {/* {Array.isArray(row.myStatus) && row.myStatus.length > 0 && (
       <div style={{ fontSize: '0.75em', color: '#007bff', marginTop: 2 }}>
         {row.myStatus.join(', ')}
       </div>
     )} */}



    </div>
  );

  return {
    position: mine ? 'right' : 'left',
    type,
    text: row.messageText || (row.attachmentURL ? row.attachmentType : ''),
    data,
    title,
    subtitle:
      Array.isArray(row.myStatus) && row.myStatus.length
        ? row.myStatus.join(', ')
        : undefined,
    date: row.createdAt ? new Date(row.createdAt) : new Date(),
  };
};

export default function IncidentAlertChat({
  incidentID,
  iAccountID,
  iCampusID,
  iBuildingID,
  buildingName = '',
  myLocation   = '',
  myStatus = [],
}) {
  const { user }       = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const scrollRef       = useRef(null);

  const loadMessages = useCallback(async (signal) => {
    if (!incidentID) return;
    try {
      const { data } = await axios.get(`/ialert/incidentmessages/${incidentID}`, { signal });
      if (!signal?.aborted && data?.success) setRows(data.messages);
    } catch (error) {
      if (!signal?.aborted && error?.code !== 'ERR_CANCELED') console.error('load messages', error);
    }
  }, [incidentID]);

  useOperationalUpdates({
    client: axios,
    path: `/ialert/incidentmessages/${encodeURIComponent(incidentID || '')}/live-events`,
    iAccountID, iBuildingID,
    app: 'IncidentAlert',
    enabled: Boolean(incidentID),
    fallbackMs: 15000,
    refreshKey: String(incidentID || ''),
    onRefresh: loadMessages,
  });

  // autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [rows]);


  // send a new message + geo
  const handleSend = async () => {
    if (busy || (!text.trim() && !file)) return;
    setBusy(true);

    // attempt geolocation
    let latitude = null, longitude = null, accuracy = null;
    try {
      const pos = await getPosition();
      latitude  = pos.coords.latitude;
      longitude = pos.coords.longitude;
      accuracy  = pos.coords.accuracy;
    } catch (err) {
      console.warn('geolocation unavailable', err);
    }

    try {
      const fd = new FormData();
      fd.append('iAccountID',         iAccountID);
      fd.append('iCampusID',          iCampusID  ?? '-99');
      fd.append('iBuildingID',        iBuildingID?? '-99');
      fd.append('senderUserID',       user.userID || user.iUserID || '');
      fd.append(
        'senderName',
        `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'Unknown',
      );
      fd.append(
        'senderMobileNumber',
        user.userMobileNumber || user.phoneNumber || user.mobileNumber || user.phone || '',
      );
      fd.append('messageText',        text);
      fd.append('myStatus',           JSON.stringify(myStatus));  
      if (file) fd.append('attachment', file);

      if (latitude  !== null) fd.append('latitude',  latitude.toString());
      if (longitude !== null) fd.append('longitude', longitude.toString());
      if (accuracy  !== null) fd.append('accuracy',  accuracy.toString());

      const { data } = await axios.post(
        `/ialert/incidentmessages/${incidentID}`,
        fd
      );
      if (data?.success && data.message) {
        setRows(prev => [...prev, data.message]);
      }
      setText(''); setFile(null);
    } catch (e) {
      console.error('send message', e);
    } finally {
      setBusy(false);
    }
  };

  // include new props in each bubble
  const dataSource = rows
    .map(r => mapRow(r, 'Me', buildingName, r.myLocation ))
    .filter(Boolean);

  return (
    <div style={{ height:'100%', display:'flex', flexDirection:'column', minHeight:0 }}>
      <div
        ref={scrollRef}
        style={{ flex:1, minHeight:0, overflowY:'auto', padding:8 }}
      >
        <MessageList className="rce-message-list" dataSource={dataSource} />
      </div>
      <Form
        className="border-top d-flex gap-2 p-2"
        onSubmit={e => { e.preventDefault(); handleSend(); }}
      >
        <Form.Control
          size="sm"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Message…"
        />
        <Button size="sm" type="submit" disabled={busy}>
          {busy ? <Spinner animation="border" size="sm" /> : 'Send'}
        </Button>
      </Form>
    </div>
  );
}



 